
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Code, Zap, Server, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchCapabilities, AgentCapability } from '@/services/agentic/capabilityService';
import { hasCapabilityTypePermission } from '@/services/agentic/permissionService';
import CapabilityList from './capabilities/CapabilityList';
import CapabilityTester from './capabilities/CapabilityTester';
import CapabilityForm from './capabilities/CapabilityForm';

const CapabilityManager = () => {
  const [capabilities, setCapabilities] = useState<AgentCapability[]>([]);
  const [selectedCapability, setSelectedCapability] = useState<AgentCapability | null>(null);
  const [loading, setLoading] = useState(true);
  const [createPermission, setCreatePermission] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [editingCapability, setEditingCapability] = useState<AgentCapability | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadCapabilities();
    checkCreatePermission();
  }, []);

  const loadCapabilities = async () => {
    setLoading(true);
    try {
      const data = await fetchCapabilities();
      setCapabilities(data);
    } catch (error) {
      console.error('Error loading capabilities:', error);
      toast.error('Failed to load capabilities');
    } finally {
      setLoading(false);
    }
  };

  const checkCreatePermission = async () => {
    try {
      // Check if user has permission to create any capability type
      const contentGenPermission = await hasCapabilityTypePermission('content_generation', 'create');
      const userMgmtPermission = await hasCapabilityTypePermission('user_management', 'create');
      const courseMgmtPermission = await hasCapabilityTypePermission('course_management', 'create');
      
      // If they have permission for any type, allow creating
      setCreatePermission(contentGenPermission || userMgmtPermission || courseMgmtPermission);
    } catch (error) {
      console.error('Error checking create permission:', error);
      setCreatePermission(false);
    }
  };

  const handleCapabilitySelected = (capability: AgentCapability) => {
    setSelectedCapability(capability);
    setActiveTab('test');
  };

  const handleEditCapability = (capability: AgentCapability) => {
    setEditingCapability(capability);
    setActiveTab('edit');
  };

  const handleCreateCapability = () => {
    if (!createPermission) {
      toast.error("You don't have permission to create capabilities");
      return;
    }
    
    setIsCreating(true);
    setActiveTab('create');
  };

  const handleCapabilityFormSuccess = (capability: AgentCapability) => {
    loadCapabilities();
    
    if (isCreating) {
      setIsCreating(false);
    } else {
      setEditingCapability(null);
    }
    
    setActiveTab('list');
  };

  const handleCancelForm = () => {
    if (isCreating) {
      setIsCreating(false);
    } else {
      setEditingCapability(null);
    }
    
    setActiveTab('list');
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>AI Capabilities</CardTitle>
            <CardDescription>
              Test and manage AI capabilities for the Bootcamp platform
            </CardDescription>
          </div>
          <Button onClick={handleCreateCapability} disabled={!createPermission}>
            <Plus className="mr-2 h-4 w-4" />
            New Capability
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="list">
              <Server className="mr-2 h-4 w-4" />
              Capabilities
            </TabsTrigger>
            <TabsTrigger value="test" disabled={!selectedCapability}>
              <Zap className="mr-2 h-4 w-4" />
              Test Capability
            </TabsTrigger>
            <TabsTrigger value="create" disabled={!isCreating}>
              <Plus className="mr-2 h-4 w-4" />
              Create Capability
            </TabsTrigger>
            <TabsTrigger value="edit" disabled={!editingCapability}>
              <Code className="mr-2 h-4 w-4" />
              Edit Capability
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="list" className="space-y-4">
            {loading ? (
              <div className="flex justify-center p-6">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              capabilities.length === 0 && !createPermission ? (
                <div className="flex flex-col items-center justify-center p-6 gap-2 text-center">
                  <AlertCircle className="h-10 w-10 text-amber-500" />
                  <h3 className="text-lg font-medium mt-2">No Access</h3>
                  <p className="text-muted-foreground max-w-md">
                    You don't have permission to view or create AI capabilities. 
                    Please contact an administrator if you believe you should have access.
                  </p>
                </div>
              ) : (
                <CapabilityList 
                  capabilities={capabilities} 
                  onSelectCapability={handleCapabilitySelected} 
                  onEditCapability={handleEditCapability}
                  onRefresh={loadCapabilities}
                />
              )
            )}
          </TabsContent>
          
          <TabsContent value="test" className="space-y-4">
            {selectedCapability && (
              <CapabilityTester capability={selectedCapability} />
            )}
          </TabsContent>
          
          <TabsContent value="create" className="space-y-4">
            {isCreating && (
              <CapabilityForm 
                onSuccess={handleCapabilityFormSuccess} 
                onCancel={handleCancelForm}
              />
            )}
          </TabsContent>
          
          <TabsContent value="edit" className="space-y-4">
            {editingCapability && (
              <CapabilityForm 
                initialData={editingCapability} 
                onSuccess={handleCapabilityFormSuccess} 
                onCancel={handleCancelForm}
              />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CapabilityManager;
